import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { CatalogGeoFacetsSchema } from './geo.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * MealService master (B4.10). Restaurant / dining experience bookable on
 * an itinerary day. Distinct from `meal_plans` (board basis BB/HB/FB/AI).
 */

export const MealKindSchema = z.enum([
  'BREAKFAST',
  'BRUNCH',
  'LUNCH',
  'HIGH_TEA',
  'DINNER',
  'SNACKS',
]);
export type MealKind = z.infer<typeof MealKindSchema>;

export const SeatingTypeSchema = z.enum(['SHARED', 'PRIVATE', 'EXCLUSIVE']);
export type SeatingType = z.infer<typeof SeatingTypeSchema>;

export const MealServiceStatusSchema = z.enum([
  'DRAFT',
  'PENDING_APPROVAL',
  'ACTIVE',
  'INACTIVE',
  'ARCHIVED',
]);
export type MealServiceStatus = z.infer<typeof MealServiceStatusSchema>;

/** A money amount: non-negative number with up to 2 decimal places. */
const PriceSchema = z.number().nonnegative();

export const MealServicePhotoSchema = z
  .object({
    /** Media-library asset id (preferred - survives presigned-URL expiry). */
    mediaId: z.string().uuid().optional(),
    /** Direct URL. Legacy imports only; new uploads store mediaId. */
    url: z.string().url().optional(),
    caption: z.string().max(200).nullable().optional(),
    alt: z.string().max(200).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((p) => Boolean(p.mediaId || p.url), { message: 'Photo needs a mediaId or a url' });
export type MealServicePhoto = z.infer<typeof MealServicePhotoSchema>;
export const MealServicePhotoListSchema = z.array(MealServicePhotoSchema);

export const MealServiceListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  legacyCode: z.string().nullable(),
  slug: z.string().nullable(),
  status: MealServiceStatusSchema,
  name: z.string(),
  mealKind: MealKindSchema,
  cuisineId: UuidSchema.nullable(),
  cuisineName: z.string().nullable(),
  restaurantName: z.string().nullable(),
  cityId: UuidSchema.nullable(),
  cityName: z.string(),
  countryName: z.string(),
  seatingType: SeatingTypeSchema,
  vendorSupplierId: UuidSchema.nullable(),
  vendorSupplierName: z.string().nullable(),
  costPerAdult: z.number(),
  currencyId: UuidSchema.nullable(),
  currencyCode: z.string().nullable(),
  isApprovedByAdmin: z.boolean(),
  photoCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type MealServiceListItem = z.infer<typeof MealServiceListItemSchema>;

export const MealServiceListQuerySchema = PaginationQuerySchema.extend({
  status: MealServiceStatusSchema.optional(),
  mealKind: MealKindSchema.optional(),
  cuisineId: UuidSchema.optional(),
  cityId: UuidSchema.optional(),
  countryId: UuidSchema.optional(),
  vendorSupplierId: UuidSchema.optional(),
  seatingType: SeatingTypeSchema.optional(),
  isApprovedByAdmin: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type MealServiceListQuery = z.infer<typeof MealServiceListQuerySchema>;

export const MealServiceListResponseSchema = paginatedResponseSchema(MealServiceListItemSchema);
export type MealServiceListResponse = z.infer<typeof MealServiceListResponseSchema>;

/** In-use countries / cities for the Meal services list filters - see
 *  {@link CatalogGeoFacetsSchema}. */
export const MealServiceFacetsSchema = CatalogGeoFacetsSchema;
export type MealServiceFacets = z.infer<typeof MealServiceFacetsSchema>;

export const MealServiceDetailSchema = MealServiceListItemSchema.extend({
  countryId: UuidSchema.nullable(),
  locationName: z.string().nullable(),
  maxCovers: z.number().int(),
  costPerChild: z.number(),
  fixedCost: z.number(),
  markupType: z.string().nullable(),
  markupValue: z.number(),
  servesAlcohol: z.boolean(),
  isVegetarianAvailable: z.boolean(),
  isVeganAvailable: z.boolean(),
  isHalalAvailable: z.boolean(),
  dressCode: z.string().nullable(),
  shortDescription: z.string().nullable(),
  longDescriptionHtml: z.string().nullable(),
  inclusionsHtml: z.string().nullable(),
  exclusionsHtml: z.string().nullable(),
  termsHtml: z.string().nullable(),
  cancellationHtml: z.string().nullable(),
  validityFrom: z.string().nullable(),
  validityTo: z.string().nullable(),
  photos: z.unknown().nullable(),
  isApprovedByModerator: z.boolean(),
  lastApprovedAt: IsoDateTimeSchema.nullable(),
  metaTagName: z.string().nullable(),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type MealServiceDetail = z.infer<typeof MealServiceDetailSchema>;

const MealServiceCoreFieldsSchema = z.object({
  status: MealServiceStatusSchema.optional(),
  legacyCode: z.string().trim().max(40).nullable().optional(),
  slug: z.string().trim().min(1).max(160).nullable().optional(),
  // Identity
  name: z.string().trim().min(1).max(300),
  mealKind: MealKindSchema.optional(),
  cuisineId: UuidSchema.nullable().optional(),
  restaurantName: z.string().trim().max(200).nullable().optional(),
  // Location - city/country chosen via the FK pickers; the name snapshots are
  // filled server-side from the selected ids.
  cityId: UuidSchema.nullable().optional(),
  cityName: z.string().trim().max(120).optional(),
  countryId: UuidSchema.nullable().optional(),
  countryName: z.string().trim().max(120).optional(),
  // Restaurant / venue as free text (no location master).
  locationName: z.string().trim().max(255).nullable().optional(),
  // Seating
  seatingType: SeatingTypeSchema.optional(),
  maxCovers: z.number().int().min(0).max(1000).optional(),
  // Pricing - plain decimal amounts in the row's currency.
  currencyId: UuidSchema.nullable().optional(),
  costPerAdult: PriceSchema.optional(),
  costPerChild: PriceSchema.optional(),
  fixedCost: PriceSchema.optional(),
  markupType: z.enum(['PERCENT', 'FLAT']).nullable().optional(),
  markupValue: PriceSchema.optional(),
  // Dietary
  servesAlcohol: z.boolean().optional(),
  isVegetarianAvailable: z.boolean().optional(),
  isVeganAvailable: z.boolean().optional(),
  isHalalAvailable: z.boolean().optional(),
  dressCode: z.string().trim().max(200).nullable().optional(),
  // Vendor
  vendorSupplierId: UuidSchema.nullable().optional(),
  // Content
  shortDescription: z.string().max(1000).nullable().optional(),
  longDescriptionHtml: z.string().max(50_000).nullable().optional(),
  inclusionsHtml: z.string().max(20_000).nullable().optional(),
  exclusionsHtml: z.string().max(20_000).nullable().optional(),
  termsHtml: z.string().max(20_000).nullable().optional(),
  cancellationHtml: z.string().max(20_000).nullable().optional(),
  // Validity
  validityFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  validityTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  // Media
  photos: MealServicePhotoListSchema.nullable().optional(),
  // Misc
  metaTagName: z.string().trim().max(200).nullable().optional(),
});

export const CreateMealServiceRequestSchema = MealServiceCoreFieldsSchema;
export type CreateMealServiceRequest = z.infer<typeof CreateMealServiceRequestSchema>;

export const UpdateMealServiceRequestSchema = MealServiceCoreFieldsSchema.partial();
export type UpdateMealServiceRequest = z.infer<typeof UpdateMealServiceRequestSchema>;

export const MealServiceApprovalRequestSchema = z.object({
  tier: z.enum(['admin', 'moderator']),
  approve: z.boolean(),
});
export type MealServiceApprovalRequest = z.infer<typeof MealServiceApprovalRequestSchema>;
